import { UseQueryOptions } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { ServiceError } from '../../../../../hooks/error';
import { useCustomMutate, useCustomQuery } from '../../../../../hooks/query';
import {
  CodeValueResponse,
  CommonErrorResponse,
  GenericErrorResponse,
  KeyValueResponse,
} from '../../../../model';
import {
  getExamLevelsOptions,
  getIndividualCandidateRegistration,
  getPaymentModeOptions,
  getResitOopAdminActionOptions,
  getResitOopAdminCoordinatorsBySchool,
  getResitOopAdminOrganizationOptions,
  getSchoolOptions,
  postDownloadOopAdminCandidateResitRegisterDetails,
  postGetOopAdminCandidateResitRegisterDetails,
  postResitOopAdminSearchCandidate,
} from '../api-services/api';
import {
  GET_OOP_ADMIN_INDIVIDUAL_RESIT_CANDIDATE_REGISTER_DETAILS_QUERY,
  GET_OOP_ADMIN_RESIT_COORDINATOR_BY_SCHOOL_QUERY,
  GET_OOP_ADMIN_RESIT_EXAM_LEVELS_OPTION_QUERY,
  GET_OOP_ADMIN_RESIT_SCHOOL_CODE_OPTION_QUERY,
  GET_OOP_ADMIN_RESIT_SCHOOL_OPTION_QUERY,
  GET_OPS_ADMIN_RESIT_ACTION_QUERY,
  GET_SCHOOL_BY_PRIVATE_CENTER_QUERY,
  POST_OOP_ADMIN_RESIT_CANDIDATE_REGISTER_DETAILS_DOWNLOAD_QUERY,
  POST_OOP_ADMIN_RESIT_CANDIDATE_REGISTER_DETAILS_QUERY,
  POST_OOP_ADMIN_RESIT_SEARCH_CANDIDATE_QUERY,
} from './keys';
import {
  CoordinatorDetails,
  FunctionAccessCheckRequest,
  OopAdminResitSearchCandidateResponse,
  OppAdminResitRegisterDetailResponse,
} from '../api-services/models';
import { OopAdminResitRegistrationDetails } from '../models';
import { BulkDownloadOrgAdminResultReleaseResponse } from '../../../result/@opadmin/models';

/** to get school options by coordinator
 * @returns KeyValueResponse[]
 * */
export const useGetOopAdminOrganizationOptions = (
  queryOptions?: UseQueryOptions<KeyValueResponse[], ServiceError> | undefined
) =>
  useCustomQuery<KeyValueResponse[]>(
    GET_OOP_ADMIN_RESIT_COORDINATOR_BY_SCHOOL_QUERY,
    () => getResitOopAdminOrganizationOptions().then((res) => res.data),
    queryOptions,
    false,
    false
  );

/** to get action options
 * @returns KeyValueResponse[]
 * */
export const useGetOopAdminActionOptions = (
  queryOptions?: UseQueryOptions<KeyValueResponse[], ServiceError> | undefined
) =>
  useCustomQuery<KeyValueResponse[]>(
    GET_OPS_ADMIN_RESIT_ACTION_QUERY,
    () => getResitOopAdminActionOptions().then((res) => res.data),
    queryOptions,
    false,
    false
  );

/**
 * to search candidate
 * @return AxiosResponse<OopAdminResitSearchCandidateResponse>
 */
export const useOopAdminSearchCandidate = (
  onSuccess: (res: AxiosResponse<OopAdminResitSearchCandidateResponse>) => void,
  onError: (res: AxiosError<CommonErrorResponse | GenericErrorResponse>) => void
) =>
  useCustomMutate<AxiosResponse<OopAdminResitSearchCandidateResponse>, any>(
    POST_OOP_ADMIN_RESIT_SEARCH_CANDIDATE_QUERY,
    postResitOopAdminSearchCandidate,
    {
      onSuccess: (res) => onSuccess(res),
      onError: (err) => onError(err),
    },
    true,
    false
  );

/**
 * to get coordinator by school
 * @reutrn AxiosResponse<OopAdminResitCoordinatorsResponse>
 */
export const useOopAdminCoordinatorsByOrganization = (
  organizationId: string,
  queryOptions?: UseQueryOptions<CoordinatorDetails[], ServiceError> | undefined
) =>
  useCustomQuery<CoordinatorDetails[]>(
    GET_SCHOOL_BY_PRIVATE_CENTER_QUERY,
    () => getResitOopAdminCoordinatorsBySchool(organizationId).then((res) => res.data),
    queryOptions,
    false,
    false
  );

/**
 * to retrieve candidate register detail
 * @return AxiosResponse<any>
 */
export const useOopAdminCandidateResitRegisterDetail = (
  onSuccess: (res: AxiosResponse<OppAdminResitRegisterDetailResponse>) => void,
  onError: (res: AxiosError<CommonErrorResponse | GenericErrorResponse>) => void
) =>
  useCustomMutate<AxiosResponse<OppAdminResitRegisterDetailResponse>, any>(
    POST_OOP_ADMIN_RESIT_CANDIDATE_REGISTER_DETAILS_QUERY,
    postGetOopAdminCandidateResitRegisterDetails,
    {
      onSuccess: (res) => onSuccess(res),
      onError: (err) => onError(err),
    },
    true,
    true
  );

/**
 * to post download data
 * @return AxiosResponse<string>
 */
export const useOopAdminCandidateResitRegisterDetailDownload = (
  onSuccess: (res: AxiosResponse<BulkDownloadOrgAdminResultReleaseResponse>) => void,
  onError: (res: AxiosError<CommonErrorResponse | GenericErrorResponse>) => void
) =>
  useCustomMutate<AxiosResponse<BulkDownloadOrgAdminResultReleaseResponse>, any>(
    POST_OOP_ADMIN_RESIT_CANDIDATE_REGISTER_DETAILS_DOWNLOAD_QUERY,
    postDownloadOopAdminCandidateResitRegisterDetails,
    {
      onSuccess: (res) => onSuccess(res),
      onError: (err) => onError(err),
    },
    true,
    false
  );

/** to get registered individual candidate detail
 * @returns OopAdminResitRegistrationDetails
 * */
export const useGetIndividualCandidateRegistration = (
  indexNo: string,
  queryOptions?: UseQueryOptions<OopAdminResitRegistrationDetails, ServiceError> | undefined
) =>
  useCustomQuery<OopAdminResitRegistrationDetails>(
    GET_OOP_ADMIN_INDIVIDUAL_RESIT_CANDIDATE_REGISTER_DETAILS_QUERY,
    () => getIndividualCandidateRegistration(indexNo).then((res) => res.data),
    queryOptions,
    false,
    false
  );

/** to get exam level options
 * @returns KeyValueResponse[]
 * */
export const useGetExamLevelOptions = (
  functionParam: FunctionAccessCheckRequest,
  queryOptions?: UseQueryOptions<CodeValueResponse[], ServiceError> | undefined
) =>
  useCustomQuery<CodeValueResponse[]>(
    GET_OOP_ADMIN_RESIT_EXAM_LEVELS_OPTION_QUERY,
    () => getExamLevelsOptions(functionParam).then((res) => res.data),
    queryOptions,
    false,
    false
  );

/** to get school options
 * @returns KeyValueResponse[]
 * */
export const useGetSchoolOptions = (
  queryOptions?: UseQueryOptions<CodeValueResponse[], ServiceError> | undefined
) =>
  useCustomQuery<CodeValueResponse[]>(
    GET_OOP_ADMIN_RESIT_SCHOOL_OPTION_QUERY,
    () => getSchoolOptions().then((res) => res.data),
    queryOptions,
    false,
    false
  );

/** to get payment mode options
 * @returns KeyValueResponse[]
 * */
export const useGetPaymentModeOptions = (
  queryOptions?: UseQueryOptions<KeyValueResponse[], ServiceError> | undefined
) =>
  useCustomQuery<KeyValueResponse[]>(
    GET_OOP_ADMIN_RESIT_SCHOOL_CODE_OPTION_QUERY,
    () => getPaymentModeOptions().then((res) => res.data),
    queryOptions,
    false,
    false
  );
